import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { addMoney, Banner, Button, Card, compareMoney, Dialog, Field, formatMoney, negate, PageHeader, Pill, type Tone } from '@elysian/ui';
import type { Account, AccountMapEntry, ImportBatch, ImportEntity } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { longDate, shortDate } from '../invoices/shared';
import { CompareWithSage } from './CompareWithSage';
import { ImportContacts } from './ImportContacts';
import { PACKAGES, packageOf, type ImportKind, type ImportPackage } from './importFile';

const ENTITY: Record<ImportEntity, string> = {
  customers: 'Customers', suppliers: 'Suppliers', opening_balances: 'Opening balances (trial balance)',
  sales_invoices: 'Unpaid sales invoices', sales_credits: 'Unused credit notes',
  purchase_invoices: 'Unpaid supplier bills', purchase_credits: 'Supplier credits',
};
const STATUS: Record<ImportBatch['status'], { tone: Tone; label: string }> = {
  staged: { tone: 'info', label: 'To check' }, validated: { tone: 'warn', label: 'Checked' },
  backing_up: { tone: 'info', label: 'Backing up first' }, completed: { tone: 'ok', label: 'Imported' }, reversed: { tone: 'draft', label: 'Reversed' },
};
// Contacts first, then what refers to them, then the balances.
const ORDER: ImportEntity[] = ['customers', 'suppliers', 'sales_invoices', 'sales_credits', 'purchase_invoices', 'purchase_credits', 'opening_balances'];

export function Import() {
  const { source, today } = useSession();
  const toast = useToast((s) => s.show);
  const [version, setVersion] = useState(0);
  // "Import data" (#16); Customers and Suppliers link here with ?what=customer or supplier.
  const [params, setParams] = useSearchParams();
  const what = params.get('what');
  const [importing, setImporting] = useState<ImportKind | null>(what === 'customer' || what === 'supplier' ? what : null);
  const closeImport = () => { setImporting(null); if (what) setParams({}, { replace: true }); setVersion((v) => v + 1); };
  const data = useAsync(async () => {
    const [list, sage, accounts] = await Promise.all([source.imports(), source.sageStatus(), source.accounts()]);
    return { ...list, sage, accounts };
  }, [source, version], { keep: true });
  const [asAt, setAsAt] = useState('2026-08-31');
  // #40: which package the trial balance comes from, and the date its balances are at (shown on step 1, not borrowed
  // from the Advanced section's migration date as before). The end of last month to start with.
  const [tbPkg, setTbPkg] = useState<ImportPackage>('sage_accounting');
  // Asked, not guessed (Brian, 31 Oct): most customers move partway through a year.
  const [tbCarry, setTbCarry] = useState(false);
  const [tbAsAt, setTbAsAt] = useState(() => { const d = today(); return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0)).toISOString().slice(0, 10); });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reload = () => setVersion((v) => v + 1);

  async function run(work: () => Promise<string | void>) {
    setBusy(true);
    setError(null);
    try {
      const msg = await work();
      if (msg) toast(msg);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const connect = () => run(async () => {
    const { authorise_url } = await source.sageConnect();
    if (authorise_url) {
      window.location.assign(authorise_url);
      return;
    }
    return 'Connected to Sage';
  });

  const pull = () => run(async () => {
    const r = await source.sagePull(asAt);
    return `Fetched from Sage: ${r.batches.map((b) => `${b.rows} ${ENTITY[b.entity!].toLowerCase()}`).join(', ')}. Check each one below.`;
  });

  const uploadTb = (file: File) => run(async () => {
    const b = await source.uploadImport(file, 'opening_balances', tbPkg, tbAsAt, tbCarry);
    const left = (b.account_map ?? []).filter((m) => !m.target_code).length;
    return `${b.row_count} trial balance lines read. ${left ? `Choose an account for the ${left === 1 ? 'code' : `${left} codes`} left blank below, then Check.` : 'Every code is matched; Check it below.'}`;
  });

  if (data.state === 'loading') return <PageHeader title="Import data" subtitle="Loading…" />;
  if (data.state === 'error') return <><PageHeader title="Import data" /><Banner>Couldn’t load: {data.error.message}</Banner></>;
  const { sage, batches, opening_balances: ob, accounts } = data.data;
  const sorted = [...batches].sort((a, b) => ORDER.indexOf(a.entity) - ORDER.indexOf(b.entity));

  return (
    <>
      <PageHeader title="Import data" subtitle="Move your books from Sage: the opening balances at a migration date, then customers, suppliers and what’s unpaid."
        actions={<Button variant="primary" onClick={() => setImporting('customer')}>Import data</Button>} />
      {importing && <div style={{ marginBottom: 20 }}><ImportContacts startWith={importing} onClose={closeImport} /></div>}
      {error && <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{error}</div>}
      {ob.as_at && (ob.balanced
        ? <Banner tone="info">Opening balances are in, as at {longDate(ob.as_at)}, and they balance.</Banner>
        : <Banner>Opening balances as at {longDate(ob.as_at)} are out by {ob.control_balance}. Import the rest, or check the trial balance.</Banner>)}

      <ol className="el-steps">
        <li data-done={batches.some((b) => b.entity === 'opening_balances')}>
          <Card as="section" padding="18px 20px" aria-labelledby="st1">
            <h2 id="st1" className="el-h2">1. Upload the trial balance</h2>
            <div className="el-form-grid" style={{ marginTop: 10 }}>
              <Field id="tb-pkg" label="From">
                <select id="tb-pkg" className="el-input" value={tbPkg} onChange={(e) => setTbPkg(e.target.value as ImportPackage)}>
                  {PACKAGES.map((x) => <option key={x.key} value={x.key} disabled={!x.tb}>{x.label}{x.tb ? '' : ' (coming later)'}</option>)}
                </select>
              </Field>
              <Field id="tb-as-at" label="Balances as at" hint="The trial balance’s date: usually the day before go-live">
                <input id="tb-as-at" type="date" className="el-input" value={tbAsAt} onChange={(e) => setTbAsAt(e.target.value)} />
              </Field>
            </div>
            <fieldset className="el-tb-mode">
              <legend>Where in your financial year is this trial balance?</legend>
              <label className="el-check"><input type="radio" name="tb-mode" checked={!tbCarry} onChange={() => setTbCarry(false)} />
                <span><strong>Partway through the year</strong><span className="el-hint">Income and costs keep their own accounts, so this year’s profit and loss carries on.</span></span></label>
              <label className="el-check"><input type="radio" name="tb-mode" checked={tbCarry} onChange={() => setTbCarry(true)} />
                <span><strong>At a year end</strong><span className="el-hint">Income and costs are last year’s profit or loss: carried to capital (retained earnings for a company) as one line.</span></span></label>
            </fieldset>
            <p className="el-hint">{packageOf(tbPkg).tbHow}</p>
            <Field id="tb-file" label="Trial balance CSV">
              <input id="tb-file" type="file" accept=".csv,text/csv" className="el-input" style={{ paddingTop: 10 }} disabled={busy || !tbAsAt || !packageOf(tbPkg).tb}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadTb(f); e.target.value = ''; }} />
            </Field>
          </Card>
        </li>
        <li data-done={batches.length > 0 && batches.every((b) => b.status === 'completed' || b.status === 'reversed')}>
          <Card as="section" padding="18px 0 8px" aria-labelledby="st2">
            <div className="el-card-head"><h2 id="st2" className="el-h2">2. Check and import, in this order</h2><span className="el-hint">A backup is taken before each import</span></div>
            {sorted.length === 0 && <p className="el-muted" style={{ padding: '0 20px' }}>Nothing read in yet.</p>}
            <div className="el-stack" style={{ gap: 0 }}>
              {sorted.map((b) => <BatchRow key={b.id} batch={b} accounts={accounts} busy={busy} run={run} />)}
            </div>
          </Card>
        </li>
      </ol>

      {/* #15: the direct connection to Sage isn't offered for now; it stays here, closed, for when it's wanted. */}
      <details className="el-advanced" open={sage.connected || undefined}>
        <summary>Advanced: connect directly to Sage Accounting</summary>
        <p className="el-hint" style={{ marginTop: 0 }}>Instead of files, the Ledger can sign in to Sage Accounting and fetch customers, suppliers and unpaid items itself.</p>
        <div className="el-stack">
          <Card as="section" padding="18px 20px" aria-labelledby="adv1">
            <h3 id="adv1" className="el-h2">Connect to Sage Accounting</h3>
            {sage.connected ? (
              <div className="el-spread" style={{ marginTop: 8 }}>
                <span>Connected to <strong>{sage.business_name}</strong>{sage.last_pulled_at && <> · last fetched {shortDate(sage.last_pulled_at.slice(0, 10))}</>}</span>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run(async () => { await source.sageDisconnect(); return 'Disconnected from Sage'; })}>Disconnect</Button>
              </div>
            ) : (
              <div className="el-spread" style={{ marginTop: 8 }}>
                <span className="el-hint">You’ll sign in to Sage and allow read access. Nothing in Sage is changed.</span>
                <Button disabled={busy} onClick={() => void connect()}>Connect to Sage</Button>
              </div>
            )}
          </Card>
          <Card as="section" padding="18px 20px" aria-labelledby="adv2">
            <h3 id="adv2" className="el-h2">Fetch contacts and unpaid items</h3>
            <p className="el-hint">Unpaid invoices and bills are taken as at the end of the migration date. What’s fetched appears in step 2 to check and import.</p>
            <div className="el-row" style={{ alignItems: 'flex-end' }}>
              <Field id="mig-date" label="Migration date"><input id="mig-date" type="date" className="el-input" value={asAt} onChange={(e) => setAsAt(e.target.value)} /></Field>
              <Button disabled={busy || !sage.connected} onClick={() => void pull()}>Fetch from Sage</Button>
            </div>
          </Card>
        </div>
      </details>
    </>
  );
}

function BatchRow({ batch, accounts, busy, run }: { batch: ImportBatch; accounts: Account[]; busy: boolean; run: (w: () => Promise<string | void>) => Promise<void> }) {
  const { source } = useSession();
  const [map, setMap] = useState<AccountMapEntry[]>((batch.account_map ?? []).map((m) => ({ ...m, target_code: m.target_code ?? '' })));
  const [createMissing, setCreateMissing] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [reason, setReason] = useState('');
  const [waitingNote, setWaitingNote] = useState<string | null>(null);
  const pkg = packageOf(batch.source_format);
  // A backup is being taken before importing (the first press of Import asks for it).
  const waiting = batch.status === 'validated' && !!batch.pre_import_backup_id && batch.backup_status !== 'completed' && batch.backup_status !== 'failed';
  const st = waiting ? STATUS.backing_up : STATUS[batch.status];
  const contactErrors = (batch.errors ?? []).some((e) => e.field === 'contact_code');
  const mapDirty = JSON.stringify(map.map((m) => m.target_code || '')) !== JSON.stringify((batch.account_map ?? []).map((m) => m.target_code ?? ''));
  const usable = accounts.filter((a) => a.is_active && a.control !== 'suspense');
  // #35: a trial balance shows every line with the account it goes to and any problem, before importing.
  const tbRows = batch.entity === 'opening_balances' && batch.rows?.length ? batch.rows : null;
  const setTarget = (code: string, target: string) => setMap((list) => list.map((x) => (x.source_code === code ? { ...x, target_code: target } : x)));

  const check = () => run(async () => {
    if (mapDirty) await source.setAccountMap(batch.id, map);
    const b = await source.validateImport(batch.id);
    return b.error_count ? `${ENTITY[batch.entity]}: ${b.error_count} ${b.error_count === 1 ? 'problem' : 'problems'} to fix` : `${ENTITY[batch.entity]}: no problems found`;
  });

  return (
    <div className="el-batch" aria-label={ENTITY[batch.entity]}>
      <div className="el-spread">
        <div><strong>{ENTITY[batch.entity]}</strong>
          <div className="el-hint">{batch.row_count} {batch.row_count === 1 ? 'row' : 'rows'}{batch.file_name && ` · ${batch.file_name}`} · from {pkg.label}{batch.as_at && ` · as at ${longDate(batch.as_at)}`}</div></div>
        <div className="el-match">
          <Pill tone={st.tone}>{st.label}</Pill>
          {(batch.status === 'staged' || batch.status === 'validated') && <Button size="sm" disabled={busy} onClick={() => void check()}>{batch.status === 'staged' ? 'Check' : 'Check again'}</Button>}
          {batch.status === 'validated' && (
            <Button size="sm" variant="primary" disabled={busy || (batch.error_count > 0 && !(contactErrors && createMissing)) || mapDirty}
              onClick={() => void run(() => importWhenBackedUp(source, batch.id, createMissing, ENTITY[batch.entity], setWaitingNote))}>
              {waiting ? 'Import once backed up' : 'Import'}</Button>
          )}
          {(batch.status === 'staged' || batch.status === 'validated') && !waiting && (
            <Button size="sm" variant="ghost" disabled={busy} aria-label={`Discard ${ENTITY[batch.entity].toLowerCase()}`}
              onClick={() => void run(async () => { await source.discardImport(batch.id); return `${ENTITY[batch.entity]} discarded: nothing was imported`; })}>Discard</Button>
          )}
          {batch.status === 'completed' && batch.entity === 'opening_balances' && !comparing && <Button size="sm" disabled={busy} onClick={() => setComparing(true)}>Compare with Sage</Button>}
          {batch.status === 'completed' && <Button size="sm" variant="ghost" disabled={busy} onClick={() => setReversing(true)}>Reverse</Button>}
        </div>
      </div>

      {comparing && batch.status === 'completed' && <CompareWithSage batch={batch} onClose={() => setComparing(false)} />}
      {waitingNote && <p className="el-hint" role="status" style={{ padding: '0 20px' }}>{waitingNote}</p>}
      {(batch.errors?.length ?? 0) > 0 && !tbRows && (
        <ul className="el-errors">
          {batch.errors!.map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}
        </ul>
      )}
      {contactErrors && batch.status === 'validated' && (
        <label className="el-check"><input type="checkbox" checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} /> Create the missing contacts from the Sage details</label>
      )}

      {tbRows && batch.status !== 'completed' && batch.status !== 'reversed' && (
        <TrialBalanceLines rows={tbRows} map={map} usable={usable} onTarget={setTarget} pkg={pkg} carry={batch.profit_to_capital !== false} />
      )}
      {!tbRows && map.length > 0 && batch.status !== 'completed' && batch.status !== 'reversed' && (
        <div className="el-table-wrap" style={{ marginTop: 10 }}>
          {batch.entity === 'opening_balances' && (
            <p className="el-hint" style={{ padding: '0 20px' }}>
              Sage’s income and cost codes (4000 to 8999) go to Capital (Retained earnings for a company): they add up to the profit or loss
              before go-live, and the new year starts with none. Choose an account for each code left blank. A bank account that isn’t
              here yet can be added under Banking first.
            </p>
          )}
          <table className="el-table" aria-label="Match Sage nominal codes to accounts">
            <thead><tr><th scope="col">{pkg.short} code</th><th scope="col">Account here</th></tr></thead>
            <tbody>
              {map.map((m, i) => (
                <tr key={m.source_code}>
                  <td><strong>{m.source_code}</strong> {m.source_name}{m.suggested && m.target_code === batch.account_map?.[i]?.target_code && <div className="el-hint">Suggested</div>}</td>
                  <td>
                    <select className="el-input" aria-label={`Account for ${m.source_code}`} value={m.target_code ?? ''} aria-invalid={!m.target_code}
                      onChange={(e) => setMap(map.map((x, j) => (j === i ? { ...x, target_code: e.target.value } : x)))}>
                      <option value="">Choose…</option>
                      {usable.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {reversing && (
        <Dialog title={`Reverse the ${ENTITY[batch.entity].toLowerCase()} import?`} onClose={() => setReversing(false)}
          actions={<><Button variant="ghost" onClick={() => setReversing(false)}>Keep it</Button>
            <Button variant="danger" disabled={busy || reason.trim().length < 3} onClick={() => void run(async () => { await source.reverseImport(batch.id, reason.trim()); setReversing(false); return 'Import reversed'; })}>Reverse import</Button></>}>
          <p style={{ margin: 0 }}>Everything this import posted is reversed by journal; nothing is deleted. Anything paid or allocated since must be undone first.</p>
          <Field id={`rev-${batch.id}`} label="Reason"><input id={`rev-${batch.id}`} className="el-input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        </Dialog>
      )}
    </div>
  );
}

/** #39: green when the account here has Sage's own code, amber when it's another account, red when there's none. */
type Match = 'same' | 'other' | 'none';
const matchOf = (source?: string | null, target?: string | null): Match => (!target ? 'none' : target === source ? 'same' : 'other');
const MATCH_TEXT: Record<Match, string> = { same: 'Same code as in the file', other: 'A different account', none: 'No account yet' };

/** Sage's income and cost codes, which together are the profit or loss before go-live (db/041). */
const isProfitAndLoss = (code?: string | null) => !!code && /^\d{4}$/.test(code) && code >= '4000' && code < '9000';

function TrialBalanceLines({ rows, map, usable, onTarget, pkg, carry }: {
  rows: NonNullable<ImportBatch['rows']>; map: AccountMapEntry[]; usable: Account[]; onTarget: (code: string, target: string) => void;
  pkg: ReturnType<typeof packageOf>;
  /** At a year end income and costs go to capital; partway through a year they keep their accounts (db/045). */
  carry: boolean;
}) {
  const codeHead = `${pkg.short} ${pkg.codeWord}`;
  const money = (v: string | number) => formatMoney(String(v));
  const dr = addMoney('0', ...rows.map((r) => String(r.debit)));
  const cr = addMoney('0', ...rows.map((r) => String(r.credit)));
  const out = addMoney(dr, negate(cr));
  const pl = !pkg.sageRanges || !carry ? '0' : addMoney('0', ...rows.filter((r) => isProfitAndLoss(r.source_code)).map((r) => addMoney(String(r.debit), negate(String(r.credit)))));
  const problems = rows.filter((r) => r.problem).length;
  return (
    <div style={{ marginTop: 10 }}>
      <p className="el-hint" style={{ padding: '0 20px' }}>
        {carry
          ? <>This trial balance is at a year end, so {pkg.sageRanges ? `${pkg.short}’s income and cost codes (4000 to 8999)` : 'income and cost lines'} go to Capital
            (Retained earnings for a company), whatever account is chosen: they add up to last year’s profit or loss, and the new year starts with none. </>
          : <>This trial balance is partway through a financial year, so each income and cost line goes to the account chosen for it, and the year’s
            profit and loss carries on from there. </>}
        Choose an account for each code left blank. A bank account that isn’t here yet can be added under Banking first.
      </p>
      {/* #39: a traffic light on each line's account. */}
      <p className="el-tb-key" aria-label="Colours">
        <span data-match="same">Same code as {pkg.short}</span>
        <span data-match="other">A different account (check it)</span>
        <span data-match="none">No account yet</span>
      </p>
      {problems > 0 && <p className="el-tb-problems" role="status">{problems} {problems === 1 ? 'line needs' : 'lines need'} attention: see below, then Check again.</p>}
      <div className="el-table-wrap">
        <table className="el-table el-tb-lines" aria-label="Trial balance lines">
          <thead><tr>
            <th scope="col">{codeHead.charAt(0).toUpperCase() + codeHead.slice(1)}</th><th scope="col" className="el-num">Debit</th><th scope="col" className="el-num">Credit</th><th scope="col">Account here</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const m = map.find((x) => x.source_code === r.source_code);
              return (
                <tr key={r.row} data-problem={r.problem ? 'true' : undefined}>
                  <td>
                    <strong>{r.source_code}</strong> {r.source_name}
                    {r.problem && <div className="el-field-error">{r.problem}</div>}
                  </td>
                  <td className="el-num">{Number(r.debit) ? money(r.debit) : ''}</td>
                  <td className="el-num">{Number(r.credit) ? money(r.credit) : ''}</td>
                  <td>
                    {m ? (
                      <select className="el-input el-tb-account" data-match={matchOf(r.source_code, m.target_code)}
                        aria-label={`Account for ${r.source_code}`} aria-description={MATCH_TEXT[matchOf(r.source_code, m.target_code)]}
                        value={m.target_code ?? ''} aria-invalid={!m.target_code}
                        onChange={(e) => onTarget(m.source_code, e.target.value)}>
                        <option value="">Choose…</option>
                        {usable.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
                      </select>
                    ) : <span>{r.account_code} {r.account_name}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Total</th><td className="el-num"><strong>{money(dr)}</strong></td><td className="el-num"><strong>{money(cr)}</strong></td>
              <td>{compareMoney(out, '0') === 0 ? <Pill tone="ok">Balances</Pill> : <Pill tone="warn">Out by {money(out.replace('-', ''))}</Pill>}</td>
            </tr>
            {compareMoney(pl, '0') !== 0 && (
              <tr><td colSpan={4} className="el-hint">
                Income and costs come to a {compareMoney(pl, '0') > 0 ? 'loss' : 'profit'} of {money(pl.replace('-', ''))}, carried to capital as one line.
              </td></tr>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/**
 * Import, taking the backup first (#40 follow-up): the first call asks for a pre-import backup, which the worker
 * takes within a couple of minutes; this waits for it, then imports. Before, the screen said "imported" after the
 * first call and nothing was posted.
 */
export async function importWhenBackedUp(source: { commitImport: (id: string, create: boolean) => Promise<ImportBatch>; importBatch: (id: string) => Promise<ImportBatch> },
  id: string, create: boolean, what: string, note: (text: string | null) => void, pollMs = IMPORT_POLL_MS, giveUpMs = 6 * 60_000): Promise<string> {
  let b = await source.commitImport(id, create);
  const started = Date.now();
  try {
    while (b.status !== 'completed') {
      if (b.backup_status === 'failed') throw new Error('The backup before importing failed, so nothing was imported. Press Import to try again.');
      if (Date.now() - started > giveUpMs) {
        throw new Error('The backup is taking longer than usual. Nothing has been imported yet: press Import again in a minute.');
      }
      note('Taking a backup first, so this import can be undone. This usually takes a minute or two…');
      await new Promise((r) => setTimeout(r, pollMs));
      b = await source.importBatch(id);
      if (b.status === 'validated' && b.backup_status === 'completed') b = await source.commitImport(id, create);
    }
  } finally {
    note(null);
  }
  return `${what} imported`;
}

/** How often the screen checks whether the backup before an import is done. */
export let IMPORT_POLL_MS = 5000;
export function setImportPollMs(ms: number) { IMPORT_POLL_MS = ms; }
